import PropTypes from "prop-types";
import React from "react";

// Shared column sorting for the admin tables. Each table keeps its own
// { sortKey, sortDir } state (class or hooks) and a getValue(row, key) accessor;
// these helpers render the header and do the actual sort so there's one impl.

// Clickable column header. Every sortable column keeps its arrow (muted while
// inactive, same as SiteTable), so the user sees which columns sort and the
// header does not shift when the active one changes.
export const SortableTh = ({
  column,
  label,
  sortKey,
  sortDir,
  onSort,
  className,
}) => {
  const active = sortKey === column;
  return (
    <th
      className={(className ?? "") + " admin-th--sortable"}
      aria-sort={
        active ? (sortDir === "asc" ? "ascending" : "descending") : "none"
      }
      onClick={() => onSort(column)}
    >
      <span>{label}</span>
      <i
        className={
          "admin-th__sort fa-solid " +
          (active
            ? sortDir === "asc"
              ? "fa-sort-up is-active"
              : "fa-sort-down is-active"
            : "fa-sort")
        }
        aria-hidden="true"
      />
    </th>
  );
};

SortableTh.propTypes = {
  column: PropTypes.string.isRequired,
  label: PropTypes.node.isRequired,
  sortKey: PropTypes.string,
  sortDir: PropTypes.string,
  onSort: PropTypes.func.isRequired,
  className: PropTypes.string,
};

// Builds a `th(column, label, className)` bound to a table's current sort state,
// so each table doesn't re-declare the same SortableTh wiring.
export const makeTh =
  (sortKey, sortDir, onSort) => (column, label, className) =>
    (
      <SortableTh
        column={column}
        label={label}
        className={className}
        sortKey={sortKey}
        sortDir={sortDir}
        onSort={onSort}
      />
    );

// Next { sortKey, sortDir } after clicking a column: same column flips
// direction, a new column starts ascending.
export const nextSort = (current, column) => ({
  sortKey: column,
  sortDir:
    current.sortKey === column && current.sortDir === "asc" ? "desc" : "asc",
});

// Stable sort of `rows` by getValue(row, sortKey); numbers compare numerically,
// everything else as a string.
export const sortRows = (rows, sortKey, sortDir, getValue) => {
  const dir = sortDir === "asc" ? 1 : -1;
  return [...rows].sort((a, b) => {
    const va = getValue(a, sortKey);
    const vb = getValue(b, sortKey);
    if (typeof va === "number" && typeof vb === "number")
      return (va - vb) * dir;
    return String(va ?? "").localeCompare(String(vb ?? "")) * dir;
  });
};
